import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { responsesService } from "../services/responses";
import { surveysService } from "../services/surveys";
import { useAuthStore } from "../store/authStore";
import QuestionCard from "../components/QuestionCard";

export default function ResponderDashboardPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Track surveys the user has skipped this session so we don't re-show them
  const [skippedSurveyIds, setSkippedSurveyIds] = useState<Set<string>>(new Set());
  // Index into the available list (acts as "next card")
  const [currentIndex, setCurrentIndex] = useState(0);
  // Track per-survey question progress: { surveyId: { questionIdx, responses[] } }
  const [surveyProgress, setSurveyProgress] = useState<
    Record<string, { questionIdx: number; responses: { question_id: string; selected_option_id: string }[] }>
  >({});
  const [rewardToast, setRewardToast] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Control exit animation after successful vote
  const [shouldCardExit, setShouldCardExit] = useState(false);

  // Fetch available surveys assigned to this responder
  const {
    data: availableData,
    isLoading,
    error: fetchError,
    refetch,
  } = useQuery({
    queryKey: ["available-surveys"],
    queryFn: () => responsesService.getAvailable({ limit: 20 }),
    refetchInterval: 30_000, // poll for new surveys
  });

  // Filter available surveys
  const availableSurveys = (availableData?.surveys || [])
    .filter((survey: any) => !skippedSurveyIds.has(survey.id));

  const currentSurvey = availableSurveys[currentIndex] ?? null;

  // Fetch full survey detail for the currently shown survey
  const { data: surveyDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ["survey", currentSurvey?.id],
    queryFn: () => surveysService.get(currentSurvey!.id),
    enabled: !!currentSurvey,
  });

  const survey = surveyDetail?.survey ?? null;
  const questions = survey?.questions ?? [];

  // Current question within the survey
  const progress = currentSurvey ? surveyProgress[currentSurvey.id] : undefined;
  const questionIdx = progress?.questionIdx ?? 0;
  const currentQuestion = questions[questionIdx] ?? null;

  const moveToNextSurvey = useCallback(() => {
    setCurrentIndex((prev) => {
      const next = prev + 1;
      return next < availableSurveys.length ? next : 0;
    });
  }, [availableSurveys.length]);

  const handleSkip = useCallback(() => {
    if (!currentSurvey) return;
    setSkippedSurveyIds((prev) => new Set(prev).add(currentSurvey.id));
    // Clear any in-progress answers for this survey
    setSurveyProgress((prev) => {
      const next = { ...prev };
      delete next[currentSurvey.id];
      return next;
    });
    setCurrentIndex(0); // reset since the filtered list shifted
  }, [currentSurvey]);

  // Submit response when user answers a question
  const handleAnswer = useCallback(
    async (optionId: string) => {
      if (!currentSurvey || !currentQuestion) return;

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        // Submit response via API
        await responsesService.respondToSurvey(currentSurvey.id, [{
          question_id: currentQuestion.id,
          selected_option_id: optionId,
          response_time_seconds: 30,
        }]);

        // Trigger exit animation
        setShouldCardExit(true);

        // After animation completes, move to next survey and update state
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["available-surveys"] });
          queryClient.invalidateQueries({ queryKey: ["my-responses"] });
          queryClient.invalidateQueries({ queryKey: ["survey", currentSurvey.id] });

          // Clear this survey from progress since vote succeeded
          setSurveyProgress((prev) => {
            const next = { ...prev };
            delete next[currentSurvey!.id];
            return next;
          });

          // Move to next survey AFTER animation completes
          moveToNextSurvey();

          // Reset exit state for next card
          setTimeout(() => {
            setShouldCardExit(false);
          }, 50);
        }, 300); // Match card animation duration (300ms)

        setRewardToast("Response recorded!");
        setTimeout(() => setRewardToast(null), 3000);
      } catch (error: any) {
        console.error("Response submission failed:", error);
        setSubmitError(error.response?.data?.error || "Failed to submit response");
        setTimeout(() => setSubmitError(null), 4000);
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentSurvey, currentQuestion, queryClient, moveToNextSurvey]
  );

  // ---------- Render states ----------

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-400">Finding questions for you...</p>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center px-4">
        <div className="text-4xl mb-4">😕</div>
        <h2 className="text-xl font-bold text-gray-100 mb-2">Something went wrong</h2>
        <p className="text-gray-400 mb-6">We couldn't load your questions. Please try again.</p>
        <button onClick={() => refetch()} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (!availableSurveys.length) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center px-4">
        <div className="text-6xl mb-4">📋</div>
        <h2 className="text-2xl font-bold text-gray-100 mb-2">No surveys available</h2>
        <p className="text-gray-400 mb-2 max-w-md">
          There are no active surveys available for responses right now. Check back soon!
        </p>
        {skippedSurveyIds.size > 0 && (
          <button
            onClick={() => {
              setSkippedSurveyIds(new Set());
              setCurrentIndex(0);
            }}
            className="btn btn-secondary mt-4"
          >
            Show skipped questions ({skippedSurveyIds.size})
          </button>
        )}
      </div>
    );
  }

  // Show loading only if detail is still loading AND we have a current survey
  if (isLoadingDetail && currentSurvey) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-400">Loading question...</p>
      </div>
    );
  }

  // If survey has no questions, show message
  if (!isLoadingDetail && currentSurvey && questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center px-4">
        <div className="text-6xl mb-4">📋</div>
        <h2 className="text-2xl font-bold text-gray-100 mb-2">No questions available</h2>
        <p className="text-gray-400 mb-2 max-w-md">
          The available survey has no questions yet. Check back soon!
        </p>
        {skippedSurveyIds.size > 0 && (
          <button
            onClick={() => {
              setSkippedSurveyIds(new Set());
              setCurrentIndex(0);
            }}
            className="btn btn-secondary mt-4"
          >
            Show skipped questions ({skippedSurveyIds.size})
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 pt-4 pb-12">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">
            Hey, {user?.email?.split("@")[0]} 👋
          </h1>
          <p className="text-sm text-gray-500">
            {availableSurveys.length} question{availableSurveys.length !== 1 ? "s" : ""} waiting
          </p>
        </div>
      </div>

      {/* Reward toast */}
      {rewardToast && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 text-green-400 rounded-xl text-center text-sm font-medium animate-pulse">
          🎉 {rewardToast}
        </div>
      )}

      {/* Error toast */}
      {submitError && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl text-center text-sm">
          {submitError}
        </div>
      )}

      {/* Question card */}
      <QuestionCard
        surveyTitle={currentSurvey.title}
        questionText={currentQuestion.question_text}
        questionNumber={questionIdx + 1}
        totalQuestions={questions.length}
        options={currentQuestion.options ?? []}
        rewardAmount={currentSurvey.reward_per_response}
        onAnswer={handleAnswer}
        onSkip={handleSkip}
        isSubmitting={isSubmitting}
        shouldExit={shouldCardExit}
      />

      {/* Progress dots (surveys) */}
      {availableSurveys.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-6">
          {availableSurveys.slice(0, 10).map((s: any, i: number) => (
            <div
              key={s.id}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentIndex ? "bg-primary-500" : "bg-dark-700"
              }`}
            />
          ))}
          {availableSurveys.length > 10 && (
            <span className="text-xs text-gray-600 ml-1">+{availableSurveys.length - 10}</span>
          )}
        </div>
      )}
    </div>
  );
}
